import styles from "./CustomizingSection.module.scss";
import { SectionCard } from "./SectionCard";

export function CustomizingSection() {
  return (
    <SectionCard
      ariaLabelledby="customizing-h"
      title="How to Make Your Work Product Dynamic"
      description="Use placeholders to automatically adapt your work product to different cases, clients, and document types."
      id="customizing"
      className={styles.customizing}
    >
      <h3>Using Placeholders</h3>
      <p>
        Placeholders are simple words or phrases surrounded by double curly
        brackets. They must be typed <strong>exactly</strong> as displayed below
        to work.
      </p>

      <h4>Example</h4>
      <p>When you type:</p>
      <pre>
        <code>
          Responding Party objects to this Interrogatory on the ground that the
          information sought is in the possession of, known to, or otherwise
          equally available to {"{{PROPOUNDING_PARTY_TYPE}}"}.
        </code>
      </pre>
      <p>
        When the propounding party is a plaintiff, it will appear in your
        document as:
      </p>
      <pre>
        <code>
          Responding Party objects to this Interrogatory on the ground that the
          information sought is in the possession of, known to, or otherwise
          equally available to Plaintiff.
        </code>
      </pre>

      <h3>Available Placeholders</h3>
      <div style={{ overflowX: "auto" }}>
        <table>
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "38%" }} />
            <col style={{ width: "28%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Placeholder</th>
              <th>Resultant Text</th>
              <th>Applies To</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>{"{{REQUEST}}"}</code>
              </td>
              <td>Request / Interrogatory</td>
              <td>All request types</td>
            </tr>
            <tr>
              <td>
                <code>{"{{REQUEST_TYPE}}"}</code>
              </td>
              <td>
                Requests for Admission, Requests for Production,
                Interrogatories, Form Interrogatories
              </td>
              <td>All request types</td>
            </tr>
            <tr>
              <td>
                <code>{"{{REQUEST_TYPE_SINGULAR}}"}</code>
              </td>
              <td>
                Request for Admission / Request for Production / Interrogatory /
                Form Interrogatory
              </td>
              <td>All request types</td>
            </tr>
            <tr>
              <td>
                <code>{"{{REQUEST_OBJECT}}"}</code>
              </td>
              <td>information / documents</td>
              <td>
                RFAs, Interrogatories, Form Interrogatories (information); RFPs
                (documents)
              </td>
            </tr>
            <tr>
              <td>
                <code>{"{{LINKING_VERB}}"}</code>
              </td>
              <td>is / are</td>
              <td>"is" for information; "are" for documents</td>
            </tr>
            <tr>
              <td>
                <code>{"{{RESPONDENT}}"}</code>
              </td>
              <td>Plaintiff / Defendant / Respondent / custom name</td>
              <td>Defined under "Refer to Responding Party As"</td>
            </tr>
            <tr>
              <td>
                <code>{"{{PROPOUNDING_PARTY_TYPE}}"}</code>
              </td>
              <td>Plaintiff / Defendant</td>
              <td>Depends on propounding party role</td>
            </tr>
            <tr>
              <td>
                <code>{"{{PROPOUNDING_PARTY}}"}</code>
              </td>
              <td>The propounding party's name</td>
              <td>Set in the "Propounding Party" confirmation page</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Additional Examples</h3>
      <p>
        <strong>1.</strong> When you type:
      </p>
      <pre>
        <code>
          The {"{{REQUEST_OBJECT}}"} sought {"{{LINKING_VERB}}"} subject to the
          attorney-client privilege.
        </code>
      </pre>
      <p>It appears as:</p>
      <pre>
        <code>
          {`The information sought is subject to the attorney-client privilege.
OR
The documents sought are subject to the attorney-client privilege.`}
        </code>
      </pre>

      <p>
        <strong>2.</strong> When you type:
      </p>
      <pre>
        <code>
          {"{{RESPONDENT}}"} objects to this interrogatory on the ground that
          the information sought is too remote from the subject matter of the
          action.
        </code>
      </pre>
      <p>It appears as:</p>
      <pre>
        <code>
          Mr. Jones objects to this interrogatory on the ground that the
          information sought is too remote from the subject matter of the
          action.
        </code>
      </pre>
    </SectionCard>
  );
}
